import Badge from '../../../common/components/badge';
import { DX_RESULT, TRANSFER_STATUS } from '../../../core/constants/reproduction';
import { envelopeData } from '../../../core/network/envelope';
import { formatDateOnly } from '../../../core/utils/date';
import { fmtNum, fmtPct } from '../../../core/utils/format';
import { useClientStats } from '../contexts/kpis';

import '../styles/report.css';

// TSS vite/03 — informe reproductivo por finca (public-reports.md,
// GET public/stats?view=report → data.report). Por cada finca del cliente:
// resumen + tabla de receptoras→transferencias con cruce, embrión, dx1/dx2,
// parto probable y badge de estado. Solo se monta cuando el módulo `report`
// viene en available_modules (partner) — el padre gatea el mount, así la
// query solo dispara para quien puede verla.
//
// El front NO recalcula: `pregnancy_rate` (tasa DIAGNOSTICADA, pregnant/
// (pregnant+empty)) viene del backend y NO es la `rates.pregnancy` del KPI
// (cohorte maduro) — dos tasas distintas, no equivalentes (contract ⚠).
// Genética recortada (genetics:'basic'): donor{id,name,number} ·
// sire{name,register,race}.

function crossLabel(transfer) {
    const donor = transfer.donor?.name || transfer.donor?.number;
    const sire = transfer.sire?.name;
    if (donor && sire) return `${donor} × ${sire}`;
    return donor || sire || '—';
}

function dxLabel(dx) {
    if (!dx) return '—';
    const result = DX_RESULT[dx.result] || dx.result || '—';
    return dx.date ? `${result} · ${formatDateOnly(dx.date)}` : result;
}

function SummaryChips({ summary }) {
    if (!summary) return null;
    return (
        <div className="informe-report__chips">
            <span className="informe-report__chip">{fmtNum(summary.total)} transferencia{summary.total === 1 ? '' : 's'}</span>
            <span className="informe-report__chip informe-report__chip--good">{fmtNum(summary.pregnant)} preñada{summary.pregnant === 1 ? '' : 's'}</span>
            <span className="informe-report__chip informe-report__chip--warn">{fmtNum(summary.transferred)} sin dx</span>
            <span className="informe-report__chip informe-report__chip--bad">{fmtNum(summary.empty)} vacía{summary.empty === 1 ? '' : 's'}</span>
            <span className="informe-report__chip informe-report__chip--rate">
                Tasa de preñez: {summary.pregnancy_rate != null ? fmtPct(summary.pregnancy_rate) : '—'}
            </span>
        </div>
    );
}

function StatusBadge({ status }) {
    const meta = TRANSFER_STATUS[status];
    return <Badge tone={meta?.tone || 'muted'}>{meta?.label || status}</Badge>;
}

// Móvil (≤860px): cards por receptora → transferencia, pares label/valor.
// Prohibido el patrón admin de tabla con scroll horizontal / columnas ocultas
// (dashboard-reuse.md §5) — el mismo dato reflowa a esta vista.
function FarmCards({ recipients }) {
    return (
        <div className="informe-report__cards">
            {recipients.map((recipient) => (
                <section key={recipient.number} className="informe-report__recipient-card">
                    <header className="informe-report__recipient-head">
                        <span className="informe-report__recipient-number">Nº {recipient.number}</span>
                        {recipient.name && (
                            <span className="informe-report__recipient-name">{recipient.name}</span>
                        )}
                    </header>

                    {(recipient.transfers || []).map((transfer, i) => (
                        <div key={i} className="informe-report__transfer">
                            <div className="informe-report__transfer-head">
                                <span className="informe-report__transfer-date">
                                    {transfer.te_date ? formatDateOnly(transfer.te_date) : 'Sin fecha'}
                                </span>
                                <StatusBadge status={transfer.status} />
                            </div>
                            <dl className="informe-report__pairs">
                                <div className="informe-report__pair">
                                    <dt>Cruce</dt>
                                    <dd>{crossLabel(transfer)}</dd>
                                </div>
                                {transfer.embryo?.code && (
                                    <div className="informe-report__pair">
                                        <dt>Embrión</dt>
                                        <dd className="informe-report__embryo">{transfer.embryo.code}</dd>
                                    </div>
                                )}
                                <div className="informe-report__pair">
                                    <dt>Dx 1</dt>
                                    <dd>{dxLabel(transfer.dx1)}</dd>
                                </div>
                                <div className="informe-report__pair">
                                    <dt>Dx 2</dt>
                                    <dd>{dxLabel(transfer.dx2)}</dd>
                                </div>
                                {transfer.due_date && (
                                    <div className="informe-report__pair">
                                        <dt>Parto probable</dt>
                                        <dd>{formatDateOnly(transfer.due_date)}</dd>
                                    </div>
                                )}
                            </dl>
                        </div>
                    ))}
                </section>
            ))}
        </div>
    );
}

// Ordenador (>860px): tabla plana por finca (receptora en la primera columna,
// mostrada en su primera fila).
function FarmTable({ recipients }) {
    const rows = recipients.flatMap((recipient) =>
        (recipient.transfers || []).map((transfer, i) => ({ recipient, transfer, first: i === 0 })),
    );

    return (
        <table className="informe-report__table">
            <thead>
                <tr>
                    <th>Receptora</th>
                    <th>T.E.</th>
                    <th>Cruce (donadora × toro)</th>
                    <th>Embrión</th>
                    <th>Dx 1</th>
                    <th>Dx 2</th>
                    <th>Parto probable</th>
                    <th>Estado</th>
                </tr>
            </thead>
            <tbody>
                {rows.map(({ recipient, transfer, first }, idx) => (
                    <tr key={`${recipient.number}-${idx}`}>
                        <td className="informe-report__recipient">
                            {first && (
                                <>
                                    <span className="informe-report__recipient-number">Nº {recipient.number}</span>
                                    {recipient.name && (
                                        <span className="informe-report__recipient-name">{recipient.name}</span>
                                    )}
                                </>
                            )}
                        </td>
                        <td>{transfer.te_date ? formatDateOnly(transfer.te_date) : '—'}</td>
                        <td className="informe-report__cross">{crossLabel(transfer)}</td>
                        <td className="informe-report__embryo">{transfer.embryo?.code || '—'}</td>
                        <td>{dxLabel(transfer.dx1)}</td>
                        <td>{dxLabel(transfer.dx2)}</td>
                        <td>{transfer.due_date ? formatDateOnly(transfer.due_date) : '—'}</td>
                        <td><StatusBadge status={transfer.status} /></td>
                    </tr>
                ))}
                {!rows.length && (
                    <tr>
                        <td colSpan={8} className="informe-report__empty-row">
                            Sin transferencias en el período.
                        </td>
                    </tr>
                )}
            </tbody>
        </table>
    );
}

function FarmReport({ block }) {
    const recipients = block.recipients || [];

    return (
        <article className="informe-report__farm">
            <header className="informe-report__farm-head">
                <h3 className="informe-report__farm-name">{block.farm?.name || 'Sin finca'}</h3>
                {block.farm?.responsible && (
                    <span className="informe-report__farm-sub">Encargado: {block.farm.responsible}</span>
                )}
            </header>

            <SummaryChips summary={block.summary} />

            {/* Mismo dato, dos layouts: tabla en ordenador, cards en móvil
                (CSS alterna por media query — sin columnas ocultas). */}
            <div className="informe-report__desktop">
                <FarmTable recipients={recipients} />
            </div>
            <div className="informe-report__mobile">
                {recipients.length
                    ? <FarmCards recipients={recipients} />
                    : <p className="informe-report__empty">Sin transferencias en el período.</p>}
            </div>
        </article>
    );
}

export default function ReportSection({ from, to }) {
    const { data: envelope, isLoading, isFetching, isError, error } = useClientStats('report', {
        from: from || undefined,
        to: to || undefined,
    });

    const report = envelopeData(envelope).report;

    if (isLoading) {
        return <div className="informe-report__state">Cargando informe por finca…</div>;
    }

    if (isError) {
        return (
            <div className="informe-report__state informe-report__state--error">
                {error?.message || 'No se pudo cargar el informe por finca.'}
            </div>
        );
    }

    if (!report) return null;

    const farms = report.farms || [];

    return (
        <section className={`informe-report ${isFetching ? 'informe-report--refreshing' : ''}`}>
            <header className="informe-report__head">
                <h2 className="informe-report__title">Informe por finca</h2>
                {report.period && (report.period.from || report.period.to) && (
                    <span className="informe-report__period">
                        {report.period.from ? formatDateOnly(report.period.from) : '…'}
                        {' — '}
                        {report.period.to ? formatDateOnly(report.period.to) : '…'}
                    </span>
                )}
            </header>

            {farms.length === 0 ? (
                <p className="informe-report__empty">Sin transferencias registradas en el período.</p>
            ) : (
                <>
                    <SummaryChips summary={report.totals} />
                    <div className="informe-report__farms">
                        {farms.map((block, i) => (
                            <FarmReport key={block.farm?.id ?? `s${i}`} block={block} />
                        ))}
                    </div>
                </>
            )}
        </section>
    );
}
